/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { WittyFormContextProvider } from '@agilelab/plugin-wb-witty-react';
import {
  errorApiRef,
  featureFlagsApiRef,
  useAnalytics,
  useApi,
  useRouteRefParams,
} from '@backstage/core-plugin-api';
import { JsonObject } from '@backstage/types';
import {
  Box,
  Button,
  StepContent,
  StepLabel,
  Stepper,
  Step as StepUI,
  Typography,
  useTheme,
} from '@material-ui/core';
import { FormProps, IChangeEvent, withTheme } from '@rjsf/core';
import { Theme as MuiTheme } from '@rjsf/material-ui';
import validator from '@rjsf/validator-ajv8';
import cloneDeep from 'lodash/cloneDeep';
import { Fragment, MutableRefObject, useEffect, useRef, useState } from 'react';
import { FormExtraConfigContextProvider } from '../../contexts/FormExtraConfigContext';
import { LayoutOptions } from '../../layouts';
import { selectedTemplateRouteRef } from '../../routes';
import { ScaffolderThemeProvider } from '../ScaffolderThemeProvider/ScaffolderThemeProvider';
import {
  DefaultArrayFieldTemplate,
  DefaultObjectFieldTemplate,
} from '../fields';
import DefaultFieldTemplate from '../fields/ArrayFieldTemplate/DefaultFieldTemplate';
import * as widgetOverrides from './WidgetOverrides';
import * as templateOverrides from './TemplateOverrides';
import { transformErrors } from './errorsOverrides';
import { transformSchemaToProps } from './schema';
import { AdditionalStep, Step } from './utils';
import { FieldExtensionOptions } from '../../extensions';
import { useDefaultFields } from './useDefaultFields';

const Form = withTheme(MuiTheme);

type Props = {
  /**
   * Steps for the form, each contains title and form schema
   */
  steps: Step[];
  formData: Record<string, any>;
  onChange: (e: IChangeEvent) => void;
  widgets?: FormProps<any>['widgets'];
  fields?: FieldExtensionOptions<any, any>[];
  layouts: LayoutOptions[];
  additionalSteps?: AdditionalStep[];
  rootStepperStyle?: JsonObject;
  stepContentStyle?: JsonObject;
  viewStepperLabel?: boolean;
  customBackButton?: JSX.Element;
  customNextButton?: JSX.Element;
  customOnSubmitFn?: () => Promise<void>;
};

export const MultiStepJsonForm = (props: Props) => {
  const {
    formData,
    onChange,
    fields,
    widgets,
    layouts,
    additionalSteps,
    rootStepperStyle,
    stepContentStyle,
    customBackButton,
    customNextButton,
    customOnSubmitFn,
    viewStepperLabel = true,
  } = props;

  const { templateName } = useRouteRefParams(selectedTemplateRouteRef);
  const theme = useTheme();
  const ref: MutableRefObject<any> = useRef(null);
  const analytics = useAnalytics();
  const [activeStep, setActiveStep] = useState(0);
  const errorApi = useApi(errorApiRef);
  const featureFlagApi = useApi(featureFlagsApiRef);
  const featureFlagKey = 'backstage:featureFlag';
  const toolbarHeight = useRef(64);

  useEffect(() => {
    const witboostToolbar = document.getElementById('witboost-toolbar');
    if (witboostToolbar) {
      toolbarHeight.current = witboostToolbar.clientHeight;
    }
  });

  /**
   * Scrolls to the top of the selected step, making it visible
   */
  const scrollToTheTopOfTheActiveStep = () => {
    // the Stepper component creates two children for every step
    const stepRefIndex = activeStep > 0 ? activeStep * 2 : 0;
    // this is the Y offset calculated as the height of WbAppToolbar + a 10 pixel padding
    const yOffset = -(toolbarHeight.current + 10);
    if (
      ref.current &&
      ref.current.children &&
      ref.current.children[stepRefIndex]
    ) {
      const element = ref.current.children[stepRefIndex];
      const yTargetScroll =
        element.getBoundingClientRect().top + window.scrollY + yOffset;
      window.scrollTo({
        top: yTargetScroll,
        behavior: 'instant' as ScrollBehavior,
      });
    }
  };

  const filterOutProperties = (step: Step): Step => {
    const filteredStep = cloneDeep(step);
    const removedPropertyKeys: Array<string> = [];
    if (filteredStep.schema.properties) {
      filteredStep.schema.properties = Object.fromEntries(
        Object.entries(filteredStep.schema.properties).filter(
          ([key, value]) => {
            if (value && value[featureFlagKey]) {
              if (featureFlagApi.isActive(value[featureFlagKey])) {
                return true;
              }
              removedPropertyKeys.push(key);
              return false;
            }
            return true;
          },
        ),
      );

      // remove the feature flag property key from required if they are not active
      filteredStep.schema.required = Array.isArray(filteredStep.schema.required)
        ? filteredStep.schema.required?.filter(
            r => !removedPropertyKeys.includes(r as string),
          )
        : filteredStep.schema.required;
    }
    return filteredStep;
  };

  const steps = props.steps
    .filter(step => {
      const featureFlag = step.schema[featureFlagKey];
      return (
        typeof featureFlag !== 'string' || featureFlagApi.isActive(featureFlag)
      );
    })
    .map(filterOutProperties);

  const handleNext = () => {
    const stepNum = Math.min(
      activeStep + 1,
      steps.length + (additionalSteps?.length ?? 0),
    );
    setActiveStep(stepNum);
    analytics.captureEvent('click', `Next Step (${stepNum})`);
  };
  const handleBack = () => setActiveStep(Math.max(activeStep - 1, 0));
  const [error, setError] = useState<any>(null);

  const usedFields = useDefaultFields(fields);

  return (
    <ScaffolderThemeProvider autoWidth>
      <Stepper
        style={{
          background: theme.palette.background.default,
          ...rootStepperStyle,
        }}
        activeStep={activeStep}
        orientation="vertical"
        ref={ref}
      >
        {steps.map(({ title, schema, asyncValidate, ...formProps }, index) => {
          const isTitleClickable = activeStep > index;
          return (
            <StepUI key={title}>
              <StepLabel
                style={{
                  cursor: isTitleClickable ? 'pointer' : 'default',
                  display: viewStepperLabel ? 'default' : 'none',
                }}
                aria-label={`Step ${index + 1} ${title}`}
                aria-disabled="false"
                tabIndex={0}
                onClick={() => {
                  if (isTitleClickable) setActiveStep(index);
                }}
              >
                <Typography variant="h6" component="h3">
                  {title}
                </Typography>
              </StepLabel>
              <StepContent
                style={{ ...stepContentStyle }}
                key={title}
                TransitionProps={{
                  // after the transition of the step, we scroll to the top of the active step
                  onExited: scrollToTheTopOfTheActiveStep,
                  // in the onExited we are invoking the scroll method that will have a smooth transition;
                  // given that the default collapse/expanse animation of the step takes a long time, we set it to 0 to reduce the latency for users
                  // so that the overall experience is not a forward-backward scroll of the page
                  timeout: {
                    appear: 0,
                    enter: 0,
                    exit: 0,
                  },
                }}
              >
                <FormExtraConfigContextProvider debounceMs={300}>
                  <WittyFormContextProvider
                    onChange={onChange}
                    formData={formData}
                    formDefinition={steps[activeStep]}
                  >
                    <Form
                      extraErrors={error}
                      templates={{
                        ArrayFieldTemplate: DefaultArrayFieldTemplate,
                        ObjectFieldTemplate: DefaultObjectFieldTemplate,
                        FieldTemplate: DefaultFieldTemplate,
                        ...templateOverrides,
                      }}
                      showErrorList={false}
                      fields={usedFields}
                      widgets={{ ...widgetOverrides, ...widgets }}
                      noHtml5Validate
                      formData={formData}
                      onChange={onChange}
                      onSubmit={async e => {
                        setError(null);
                        if (asyncValidate) {
                          const validationResult = await asyncValidate(
                            formData,
                          );
                          if (validationResult) {
                            const keysWithErrors = Object.keys(
                              validationResult,
                            ).filter(
                              key => validationResult[key]?.__errors?.length,
                            );

                            if (
                              keysWithErrors.length === 0 &&
                              e.errors.length === 0
                            ) {
                              if (typeof customOnSubmitFn === 'function') {
                                await customOnSubmitFn();
                              }
                              handleNext();
                            } else {
                              setError(validationResult);
                            }
                          } else {
                            if (e.errors.length === 0) {
                              if (typeof customOnSubmitFn === 'function') {
                                await customOnSubmitFn();
                              }
                              handleNext();
                            }
                          }
                        } else {
                          if (e.errors.length === 0) {
                            if (typeof customOnSubmitFn === 'function') {
                              await customOnSubmitFn();
                            }
                            handleNext();
                          }
                        }
                      }}
                      {...formProps}
                      {...transformSchemaToProps(schema, layouts)}
                      formContext={formData}
                      transformErrors={transformErrors}
                      validator={validator}
                    >
                      {customBackButton ?? (
                        <Button
                          disabled={activeStep === 0 && steps.length > 1}
                          onClick={handleBack}
                        >
                          Back
                        </Button>
                      )}
                      {customNextButton ?? (
                        <Button
                          variant="contained"
                          color="primary"
                          type="submit"
                        >
                          Next step
                        </Button>
                      )}
                    </Form>
                  </WittyFormContextProvider>
                </FormExtraConfigContextProvider>
              </StepContent>
            </StepUI>
          );
        })}
        {additionalSteps?.map(
          (
            {
              title,
              component,
              additionalActions,
              backButtonText,
              nextButtonText,
              disableStepButtons,
              customDisableStepButtons,
              resetButtonText,
              analyticsEvent,
              resetButtonColor,
              resetButtonVariant,
              resetAction,
              backAction,
              nextAction,
            },
            index,
          ) => {
            const isTitleClickable = activeStep > index + steps.length;
            return (
              <StepUI key={title}>
                <StepLabel
                  style={{
                    cursor: isTitleClickable ? 'pointer' : 'default',
                  }}
                  aria-label={`Step ${index + 1} ${title}`}
                  aria-disabled="false"
                  tabIndex={0}
                  onClick={() => {
                    if (isTitleClickable) setActiveStep(steps.length + index);
                  }}
                >
                  <Typography variant="h6" component="h3">
                    {title}
                  </Typography>
                </StepLabel>
                <StepContent
                  key={title}
                  TransitionProps={{
                    // after the transition of the step, we scroll to the top of the active step
                    onExited: scrollToTheTopOfTheActiveStep,
                    // in the onExited we are invoking the scroll method that will have a smooth transition;
                    // given that the default collapse/expanse animation of the step takes a long time, we set it to 0 to reduce the latency for users
                    // so that the overall experience is not a forward-backward scroll of the page
                    timeout: {
                      appear: 0,
                      enter: 0,
                      exit: 0,
                    },
                  }}
                >
                  <Box>
                    {component}
                    {(resetButtonText || nextButtonText || backButtonText) && (
                      <Box mb={3} />
                    )}
                    <Box style={{ display: 'flex', alignItems: 'center' }}>
                      {backButtonText && (
                        <Button
                          disabled={
                            customDisableStepButtons?.disableBack ??
                            disableStepButtons
                          }
                          onClick={() => {
                            if (backAction) backAction();
                            handleBack();
                          }}
                        >
                          {backButtonText}
                        </Button>
                      )}
                      {resetButtonText && (
                        <Button
                          variant={resetButtonVariant}
                          color={resetButtonColor}
                          onClick={() => {
                            if (resetAction) resetAction();
                            setActiveStep(0);
                          }}
                          disabled={
                            customDisableStepButtons?.disableReset ??
                            disableStepButtons
                          }
                        >
                          {resetButtonText}
                        </Button>
                      )}
                      {nextButtonText && (
                        <Button
                          variant="contained"
                          color="primary"
                          disabled={
                            customDisableStepButtons?.disableNext ??
                            disableStepButtons
                          }
                          onClick={async () => {
                            try {
                              if (nextAction) await nextAction();
                              if (analyticsEvent) {
                                analytics.captureEvent(
                                  analyticsEvent,
                                  formData.name || `new ${templateName}`,
                                );
                              }
                              handleNext();
                            } catch (err) {
                              errorApi.post(err);
                            }
                          }}
                        >
                          {nextButtonText}
                        </Button>
                      )}
                      {additionalActions?.map(action => (
                        <Fragment key={action.key}>{action}</Fragment>
                      ))}
                    </Box>
                  </Box>
                </StepContent>
              </StepUI>
            );
          },
        )}
      </Stepper>
    </ScaffolderThemeProvider>
  );
};
